import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { Star, ChevronLeft, ChevronRight, MessageSquare } from "lucide-react";
import { siteConfig } from "../siteConfig";
import { useQuoteModal } from "../context/QuoteModalContext";
import Reveal from "./Reveal";

/* Use all 6 reviews, split into two pages of 3 */
const ALL_REVIEWS = siteConfig.reviews.slice(0, 6);
const PAGES = [ALL_REVIEWS.slice(0, 3), ALL_REVIEWS.slice(3, 6)];

const GoogleG = () => (
  <svg width="26" height="26" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const ReviewCard = ({ review }) => (
  <div className="bg-white rounded-2xl p-6 shadow-xl flex flex-col h-full">
    <div className="flex gap-1 mb-3">
      {[...Array(review.rating ?? 5)].map((_, i) => (
        <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
      ))}
    </div>
    <p className="text-gray-700 text-sm leading-relaxed flex-1 mb-5">{review.text}</p>
    <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
      <div>
        <p className="font-bold text-sm" style={{ color: siteConfig.colors.primary }}>{review.name}</p>
        <p className="text-gray-400 text-xs">Verified Google Review</p>
      </div>
      <GoogleG />
    </div>
  </div>
);

const HomeReviewsSection = () => {
  const { open: openModal } = useQuoteModal();

  // UI state (drives renders)
  const [page,      setPage]      = useState(0);
  const [animState, setAnimState] = useState("idle");
  const [dir,       setDir]       = useState(1);

  // Refs — read synchronously without stale-closure issues
  const pageRef      = useRef(0);
  const animRef      = useRef("idle"); // mirrors animState but always current
  const animTimerRef = useRef(null);
  const autoTimerRef = useRef(null);
  const gridRef      = useRef(null);

  const setAnim = useCallback((s) => { animRef.current = s; setAnimState(s); }, []);

  // After React commits "entering" DOM, force reflow then fade in.
  // Timer stored in animTimerRef so useLayoutEffect cleanup never cancels it.
  useLayoutEffect(() => {
    if (animState !== "entering") return;
    void gridRef.current?.offsetWidth; // commit starting position to browser
    setAnim("in");
    clearTimeout(animTimerRef.current);
    animTimerRef.current = setTimeout(() => setAnim("idle"), 400);
  }, [animState, setAnim]);

  // Cleanup anim timer on unmount
  useEffect(() => () => clearTimeout(animTimerRef.current), []);

  const goTo = useCallback((idx, d = 1) => {
    if (animRef.current !== "idle") return;
    pageRef.current = idx;
    setDir(d);
    setAnim("out");
    setTimeout(() => {
      setPage(idx);
      setAnim("entering");
    }, 280);
  }, [setAnim]);

  // next/prev use refs so they never go stale
  const next = useCallback(() => goTo((pageRef.current + 1) % PAGES.length,  1),  [goTo]);
  const prev = useCallback(() => goTo((pageRef.current - 1 + PAGES.length) % PAGES.length, -1), [goTo]);

  const resetAutoPlay = useCallback(() => {
    clearInterval(autoTimerRef.current);
    autoTimerRef.current = setInterval(next, 5500);
  }, [next]);

  useEffect(() => {
    autoTimerRef.current = setInterval(next, 5500);
    return () => clearInterval(autoTimerRef.current);
  }, [next]);

  const handleGoTo = (idx) => { goTo(idx, idx > pageRef.current ? 1 : -1); resetAutoPlay(); };
  const handlePrev  = () => { prev(); resetAutoPlay(); };
  const handleNext  = () => { next(); resetAutoPlay(); };

  const gridStyle = {
    idle:     { opacity: 1, transform: "translateX(0)",               transition: "none" },
    out:      { opacity: 0, transform: `translateX(${dir * 30}px)`,   transition: "opacity 0.28s ease, transform 0.28s ease" },
    entering: { opacity: 0, transform: `translateX(${-dir * 30}px)`,  transition: "none" },
    in:       { opacity: 1, transform: "translateX(0)",               transition: "opacity 0.38s cubic-bezier(0.4,0,0.2,1), transform 0.38s cubic-bezier(0.4,0,0.2,1)" },
  }[animState];

  return (
    <section className="relative overflow-hidden" style={{ minHeight: "600px" }}>

      {/* Background photo */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${siteConfig.cta.image})` }}
      />
      <div className="absolute inset-0" style={{ background: "rgba(4,12,4,0.76)" }} />

      {/* Green wave top */}
      <div className="absolute top-0 left-0 right-0 leading-none pointer-events-none">
        <svg viewBox="0 0 1440 88" preserveAspectRatio="none" style={{ width: "100%", height: "88px", display: "block" }}>
          <path
            d="M0,0 C180,88 360,0 540,44 C720,88 900,8 1080,44 C1260,80 1350,16 1440,44 L1440,0 Z"
            fill={siteConfig.colors.primaryDark}
          />
        </svg>
      </div>

      {/* Green wave bottom */}
      <div className="absolute bottom-0 left-0 right-0 leading-none pointer-events-none">
        <svg viewBox="0 0 1440 88" preserveAspectRatio="none" style={{ width: "100%", height: "88px", display: "block" }}>
          <path
            d="M0,44 C180,8 360,80 540,44 C720,8 900,88 1080,44 C1260,0 1380,72 1440,44 L1440,88 L0,88 Z"
            fill={siteConfig.colors.primaryDark}
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-28 pb-14">

        {/* Top row: badge + headline + rule + CTA button */}
        <Reveal as="div" className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div className="flex-1">
            <div
              className="inline-block text-white text-xs font-black uppercase tracking-widest px-4 py-2 mb-5 rounded-sm"
              style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
            >
              What Homeowners Are Saying
            </div>
            <div className="flex items-center gap-6">
              <h2
                className="font-display font-black text-white shrink-0"
                style={{ fontSize: "clamp(3rem,6.5vw,5.5rem)", lineHeight: 1 }}
              >
                Reviews
              </h2>
              <div
                className="hidden md:block flex-1 h-0.5 rounded-full"
                style={{ backgroundColor: siteConfig.colors.primary }}
              />
            </div>
          </div>

          <button
            onClick={openModal}
            className="hidden md:inline-flex items-center gap-3 bg-white rounded-xl px-6 py-4 font-bold text-sm shadow-xl hover:bg-gray-50 transition-colors shrink-0"
            style={{ color: siteConfig.colors.dark }}
          >
            <MessageSquare className="w-5 h-5" style={{ color: siteConfig.colors.primary }} />
            Get Free Quote
            <ChevronRight className="w-4 h-4" />
          </button>
        </Reveal>

        {/* ── Carousel ── */}
        <div className="overflow-hidden">
          <div
            ref={gridRef}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
            style={gridStyle}
          >
            {PAGES[page].map((review, i) => (
              <ReviewCard key={`${page}-${i}`} review={review} />
            ))}
          </div>
        </div>

        {/* Dots + prev/next */}
        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={handlePrev}
            className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white hover:bg-white/15 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {PAGES.map((_, i) => (
            <button
              key={i}
              onClick={() => handleGoTo(i)}
              className="rounded-full"
              style={{
                width: i === page ? "26px" : "8px",
                height: "8px",
                backgroundColor: i === page
                  ? siteConfig.colors.primary
                  : "rgba(255,255,255,0.35)",
                transition: "width 0.35s cubic-bezier(0.4,0,0.2,1), background-color 0.35s ease",
              }}
            />
          ))}

          <button
            onClick={handleNext}
            className="w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white hover:bg-white/15 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Review Us on Google */}
        <div className="flex justify-center mt-10">
          <Reveal
            className="rounded-2xl px-12 py-8 text-center border border-white/10 w-full max-w-lg"
            style={{ backgroundColor: `${siteConfig.colors.dark}CC`, backdropFilter: "blur(14px)" }}
          >
            <h3 className="font-display font-black text-white text-2xl mb-3">Review Us on Google</h3>
            <p className="text-white/70 text-sm mb-5">
              We'd love it if you left us a review!
            </p>
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-7 h-7 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <Link
              to="/review"
              className="inline-flex items-center gap-2 px-8 h-12 font-bold text-white rounded-xl text-sm transition-opacity hover:opacity-85"
              style={{ backgroundColor: siteConfig.colors.primaryDark }}
            >
              Leave a Review <ChevronRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

      </div>
    </section>
  );
};

export default HomeReviewsSection;
